import React, { useState, useRef, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getPageIdFromPath } from "../../routes/routeConfig";
import Logo from "../ui/Logo";
import {
  LayoutDashboard, FileSearch, Eye, BarChart3,
  Sparkles, FlaskConical, Swords, Crown, LogOut, ChevronDown,
  Layers, Beaker, User, Microscope, Lock, ArrowRight,
} from "lucide-react";
import { canAccessFeature, getMinimumPlanForFeature } from "../../utils/featureAccess";

const CORE_NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, requiresAuth: true },
  { id: "audits", label: "Audits", icon: FileSearch, requiresAuth: false },
  { id: "ai-visibility-lab", label: "AI Visibility Lab", icon: Microscope, requiresAuth: false },
];

const TOOLS_DROPDOWN = [
  { id: "monitor", label: "Monitor Pages", icon: Eye, requiresAuth: true },
  { id: "reports", label: "Reports", icon: BarChart3, requiresAuth: true },
  { id: "advanced", label: "Advanced Audit", icon: Sparkles, requiresAuth: true, premiumFeature: "advanced_audit" },
  { id: "cli", label: "CLI Tool", icon: Microscope, requiresAuth: true, premiumFeature: "cli_tool" },
  { id: "simulator", label: "Strategy Simulator", icon: FlaskConical, requiresAuth: true, premiumFeature: "strategy_simulator" },
];

const ENTERPRISE_DROPDOWN = [
  { id: "compare", label: "Competitor Intel", icon: Swords, requiresAuth: true, premiumFeature: "competitor_intel" },
  { id: "executive", label: "Executive Summary", icon: Crown, requiresAuth: true, isEnterprise: true },
];

function DropdownMenu({ label, icon: Icon, items, activePage, onNavigate, onShowFeatureLocked }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { user } = useAuth();
  const isActive = items.some((item) => item.id === activePage);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleItemClick = (item) => {
    setOpen(false);

    if (item.requiresAuth && !user) {
      onNavigate("login");
      return;
    }

    if (item.premiumFeature && !canAccessFeature(user, item.premiumFeature)) {
      onShowFeatureLocked && onShowFeatureLocked(item.id);
      return;
    }

    onNavigate(item.id);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`nav-dropdown-${label.toLowerCase()}`}
        className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isActive
          ? "bg-white/10 text-white font-semibold"
          : "text-muted-foreground hover:text-white hover:bg-white/5"
          }`}
        style={isActive ? { borderLeft: "2px solid var(--primary)", paddingLeft: "calc(0.875rem - 2px)" } : undefined}
      >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 min-w-[200px] glass-dropdown rounded-xl overflow-hidden animate-fadeIn z-50">
          {items.map((item) => {
            const ItemIcon = item.icon;
            const isItemActive = activePage === item.id;
            const isPremiumLocked = Boolean(item.premiumFeature) && !canAccessFeature(user, item.premiumFeature);
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => handleItemClick(item)}
                title={isPremiumLocked ? `Available in ${getMinimumPlanForFeature(item.premiumFeature)}` : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all duration-200 ${isItemActive
                  ? "bg-white/10 text-white font-semibold"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
                  }`}
                style={
                  isItemActive
                    ? {
                      borderLeft: "2px solid var(--primary)",
                      paddingLeft: "calc(1rem - 2px)",
                      opacity: isPremiumLocked ? 0.62 : 1,
                    }
                    : { opacity: isPremiumLocked ? 0.62 : 1 }
                }
              >
                <ItemIcon className="w-4 h-4" />
                {item.label}
                {isPremiumLocked && <Lock className="w-3.5 h-3.5 ml-auto text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Navbar({ activePage, onNavigate, isLanding = false, onGetStarted, onShowFeatureLocked, onLogout }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const resolvedActivePage = activePage || getPageIdFromPath(pathname);
  const [scrolled, setScrolled] = useState(false);
  const reduceMotion = useReducedMotion();

  // Add scroll listener for glass effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogoClick = () => {
    if (isLanding) {
      // On landing page, scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Navigate to landing page
      onNavigate && onNavigate("landing");
    }
  };

  // Handle navigation with access control
  const handleNavClickWithAuth = (itemId, requiresAuth = false) => {
    // Public items - always allow
    if (!requiresAuth) {
      onNavigate(itemId);
      return;
    }

    // Requires auth but user not logged in
    if (!user) {
      onNavigate("login");
      return;
    }

    // Logged in - navigate
    onNavigate(itemId);
  };

  const renderGuestActions = () => (
    <div className="flex items-center gap-3 pl-4 border-l border-white/10 ml-3">
      <button
        data-testid="nav-login-btn"
        onClick={() => onNavigate && onNavigate("login")}
        className="btn-secondary text-sm px-5 py-2"
      >
        Sign In
      </button>
      <button
        data-testid="nav-get-started-btn"
        onClick={onGetStarted}
        className="btn-primary text-sm px-5 py-2"
      >
        Get Started
      </button>
    </div>
  );

  // Landing page navbar - floating pill specifically matching the new design
  if (isLanding) {
    const LANDING_NAV = [
      { id: "platform", label: "Platform" },
      { id: "how-it-works", label: "How it works" },
      { id: "cli", label: "CLI" },
      { id: "pricing", label: "Pricing" },
      { id: "faq", label: "FAQ" },
    ];

    return (
      <motion.header
        className="fixed top-0 left-0 right-0 z-50 flex justify-center py-4 md:py-5 px-4 md:px-8 transition-all duration-300 ease-out"
        data-testid="navbar"
        style={{ transform: 'translateZ(0)' }}
        initial={reduceMotion ? false : { opacity: 0, y: -8 }}
        animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className="flex items-center justify-between w-full max-w-[1280px]">
          {/* Left: Logo & Live Badge */}
          <div className="flex items-center gap-5">
            <div
              className="flex items-center gap-2 cursor-pointer hover:opacity-85 transition-opacity"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              data-testid="nav-logo-landing"
            >
              <Logo size="sm" />
              <span className="brand-name font-bold text-[18px] tracking-tight text-white whitespace-nowrap">
                Scopuz
              </span>
            </div>


          </div>

          {/* Center: Scroll-spy Navigation */}
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/5 bg-[#09090b]/80 backdrop-blur-md">
            {LANDING_NAV.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  const el = document.getElementById(item.id);
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-3.5 py-1.5 rounded-full text-[13px] font-medium text-[#c0c0c6] hover:text-white transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-5">
            {!user ? (
              <button
                data-testid="nav-login-btn"
                onClick={() => onNavigate("login")}
                className="text-[13px] font-medium text-muted-foreground hover:text-white transition-colors hidden sm:block"
              >
                Sign in
              </button>
            ) : (
              <button
                data-testid="nav-dashboard-btn"
                onClick={() => onNavigate("dashboard")}
                className="text-[13px] font-medium text-muted-foreground hover:text-white transition-colors hidden sm:block"
              >
                Dashboard
              </button>
            )}

            <button
              data-testid="nav-get-started-btn"
              onClick={() => onNavigate("audits")}
              className="group flex items-center gap-3 bg-primary hover:bg-primary-hover text-[#09090b] pl-5 pr-1.5 py-1.5 rounded-full text-[13px] font-bold transition-all"
            >
              Run Audit
              <span className="bg-[#09090b] text-primary p-1.5 rounded-full group-hover:rotate-45 transition-transform duration-300">
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </button>
          </div>
        </div>
      </motion.header>
    );
  }

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50 flex justify-center py-4 px-4 transition-all duration-300 ease-out"
      data-testid="navbar"
      style={{ transform: 'translateZ(0)' }}
      initial={reduceMotion ? false : { opacity: 0, y: -8 }}
      animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <nav className={`flex items-center gap-1 rounded-full border px-6 py-2.5 transition-all duration-300 ease-out ${scrolled
        ? 'bg-black/70 backdrop-blur-xl border-white/25 shadow-2xl'
        : 'bg-black/30 backdrop-blur-lg border-white/15 shadow-lg'
        }`} style={{ opacity: scrolled ? 1 : 0.94 }}>
        {/* Logo */}
        <div
          className="flex items-center gap-2 pr-4 border-r border-white/10 mr-3 cursor-pointer hover:opacity-85 transition-all duration-200 ease-out"
          onClick={handleLogoClick}
          data-testid="nav-logo-app"
          title="Return to home"
        >
          <Logo size="sm" />
          <span className="hidden sm:inline brand-name font-bold text-sm tracking-tight text-white whitespace-nowrap">
            Scopuz
          </span>
        </div>

        {/* Core Navigation */}
        <div className="hidden md:flex items-center gap-0.5">
          {CORE_NAV.map((item) => {
            const Icon = item.icon;
            const isActive = resolvedActivePage === item.id;
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => handleNavClickWithAuth(item.id, item.requiresAuth)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isActive
                  ? "bg-white/10 text-white font-semibold"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
                  }`}
                style={isActive ? { borderLeft: "2px solid var(--primary)", paddingLeft: "calc(0.875rem - 2px)" } : undefined}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden md:inline">{item.label}</span>
              </button>
            );
          })}

          {/* Tools Dropdown */}
          <DropdownMenu
            label="Tools"
            icon={Layers}
            items={TOOLS_DROPDOWN}
            activePage={resolvedActivePage}
            onNavigate={onNavigate}
            onShowFeatureLocked={onShowFeatureLocked}
          />

          {/* Enterprise Dropdown */}
          <DropdownMenu
            label="Enterprise"
            icon={Beaker}
            items={ENTERPRISE_DROPDOWN}
            activePage={resolvedActivePage}
            onNavigate={onNavigate}
            onShowFeatureLocked={onShowFeatureLocked}
          />
        </div>

        {/* User Section */}
        {user && (
          <div className="flex items-center gap-2 pl-4 border-l border-white/10 ml-3">
            <button
              data-testid="nav-profile"
              onClick={() => onNavigate("profile")}
              className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${resolvedActivePage === "profile"
                ? "bg-white/10 text-white font-semibold"
                : "text-muted-foreground hover:text-white hover:bg-white/5"
                }`}
              style={resolvedActivePage === "profile" ? { borderLeft: "2px solid var(--primary)", paddingLeft: "calc(0.75rem - 2px)" } : undefined}
            >
              <User className="w-4 h-4" />
              <span className="hidden lg:inline">Profile</span>
            </button>
            <button
              data-testid="logout-button"
              onClick={onLogout}
              className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-2 text-sm font-medium text-muted-foreground hover:text-red-400 hover:border-red-400/30 hover:bg-red-400/5 transition-all duration-200"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        )}

        {/* Guest Auth Buttons */}
        {!user && renderGuestActions()}
      </nav>
    </motion.header>
  );
}
